import { createContext, FC, ReactNode, useContext } from "react";

/**
 * Navigation function required by shared components in `@tsmono/react`.
 *
 * Each host app provides this via `<ComponentNavigationProvider>` inside its
 * router tree, mapping its own navigation implementation (react-router,
 * debug-wrapped navigate, etc.) to this simple interface.
 *
 * Unlike `ComponentStateProvider`, which sits outside
 * the router, this provider must live **inside** the router tree because
 * `useNavigate()` requires router context.
 */
export interface ComponentNavigation {
  navigate: (
    path: string,
    options?: { replace?: boolean }
  ) => void | Promise<void>;
}

const ComponentNavigationContext = createContext<ComponentNavigation | null>(
  null
);

export const ComponentNavigationProvider: FC<{
  navigation: ComponentNavigation;
  children: ReactNode;
}> = ({ navigation, children }) => (
  <ComponentNavigationContext.Provider value={navigation}>
    {children}
  </ComponentNavigationContext.Provider>
);

export const useComponentNavigation = (): ComponentNavigation => {
  const navigation = useContext(ComponentNavigationContext);
  if (!navigation) {
    throw new Error(
      "useComponentNavigation must be used within a ComponentNavigationProvider"
    );
  }
  return navigation;
};
